import { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Switch } from '@/components/ui/switch';

const formatRelative = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffSec = Math.max(0, Math.round((now - then) / 1000));
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} min${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hr${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 7) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const dt = new Date(iso);
  return dt.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Operational toggle tile used inside the Competition Page Editor.
 *
 * Renders the standard "control tile" layout used by Upload Entries,
 * Music and Content Sheet Submission, and the Questions or Comments
 * Form: header + icon on the left, tagline below, and a "Control
 * Active" Switch on the right.
 *
 * Note on the *off* state: this component is editor-side only — what
 * actually shows on the live Competition Page when a control is off
 * is the responsibility of `PublicCompetitionPage.js`. Two of the
 * three toggles leave a closed-notice tile in place; the
 * Questions/Comments toggle simply hides the form. That asymmetry is
 * deliberate, and lives in the public page, not here.
 *
 * Props:
 *   icon           — Lucide icon component (e.g. Upload, Music, MessageCircle)
 *   header         — tile header text
 *   tagline        — short description shown below the header
 *   checked        — current value of the toggle
 *   onChange       — async setter (receives the new boolean)
 *   disabled       — disables the switch while a save is in flight
 *   testId         — applied to the underlying Switch
 *   tooltipTitle   — optional title attribute for the label wrapper
 *   pageId         — competition_page_id; when provided alongside ``controlKind`` the tile
 *                    fetches and renders the most recent audit record (who flipped it & when)
 *   controlKind    — one of "uploads_enabled" | "music_submissions_enabled" | "inquiry_form_enabled"
 */
export default function PageControlToggle({
  icon: Icon,
  header,
  tagline,
  checked,
  onChange,
  disabled = false,
  testId,
  tooltipTitle,
  pageId,
  controlKind,
}) {
  const [audit, setAudit] = useState(null);

  const refreshAudit = async () => {
    if (!pageId || !controlKind) return;
    try {
      const res = await axios.get(
        `${API}/competition-pages/${pageId}/control-audit/${controlKind}`,
        { withCredentials: true }
      );
      setAudit(res.data?.last_change || null);
    } catch {
      setAudit(null);
    }
  };

  useEffect(() => {
    refreshAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId, controlKind]);

  // Reflect every toggle flip — checked changes after onChange resolves
  // so this is the cheapest way to keep the audit line fresh without
  // threading a refresh callback up to the parent.
  useEffect(() => {
    if (pageId && controlKind) {
      refreshAudit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked]);

  const handleChange = async (next) => {
    await onChange(next);
    refreshAudit();
  };

  return (
    <div className="p-4 border rounded-lg bg-stone-50">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <h3 className="font-semibold text-stone-800 mb-2 flex items-center gap-2">
            {Icon ? <Icon className="h-4 w-4" /> : null}
            {header}
          </h3>
          <p className="text-sm text-stone-600">{tagline}</p>
          {audit && (
            <p className="text-xs text-stone-500 mt-2" data-testid={testId ? `${testId}-audit` : undefined}>
              {audit.new_value ? 'Enabled' : 'Disabled'} by {audit.user_name || 'unknown'} · {formatRelative(audit.changed_at)}
            </p>
          )}
        </div>
        <label
          className="flex items-center gap-2 select-none whitespace-nowrap"
          title={tooltipTitle}
        >
          <span className="text-sm font-medium text-stone-700">Control Active</span>
          <Switch
            checked={checked}
            onCheckedChange={handleChange}
            disabled={disabled}
            data-testid={testId}
          />
        </label>
      </div>
    </div>
  );
}
